import { createClient } from '@sanity/client';
import imageUrlBuilder from '@sanity/image-url';

const token = process.env.REACT_APP_SANITY_TOKEN;

if (!token) {
  console.error('No Sanity token found in environment variables. Please check your .env file.');
}

export const client = createClient({
  projectId: 'mv1p3jun',
  dataset: 'production',
  apiVersion: '2024-03-19',
  useCdn: false,
  token: token,
  ignoreBrowserTokenWarning: true,
});

const builder = imageUrlBuilder(client);

export const urlFor = (source) => builder.image(source);

// Helper function to fetch site-wide settings (social share thumbnail, etc.)
export const fetchSiteSettings = async () => {
  const query = `*[_type == "siteSettings"][0] {
    "shareThumbnail": shareThumbnail.asset->url,
    "siteIcon": siteIcon.asset->url
  }`;

  try {
    return await client.fetch(query);
  } catch (error) {
    console.error('Error fetching site settings from Sanity:', error);
    throw error;
  }
};

// Helper function to fetch about page content
export const fetchAboutContent = async () => {
  const query = `*[_type == "about"][0] {
    name,
    socials[] {
      platform,
      url
    },
    contacts[] {
      type,
      value
    },
    paragraphs,
    logos[] {
      asset-> {
        url
      },
      alt
    }
  }`;
  
  try {
    const result = await client.fetch(query);
    
    if (!result) {
      console.warn('No about document found in Sanity. Please create one in the Sanity Studio.');
      return null;
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching from Sanity:', {
      message: error.message,
      details: error.details,
      statusCode: error.statusCode,
      response: error.response,
      stack: error.stack,
      token: token ? 'Token is present' : 'No token found',
      config: client.config()
    });
    throw error;
  }
};

export async function fetchCaseStudy(slug) {
  const query = `*[_type == "caseStudy" && slug.current == $slug][0]{
    titleLineOne,
    titleLineTwo,
    date,
    tags,
    slug,
    heroImage {
      desktop {
        asset-> {
          url
        }
      },
      mobile {
        asset-> {
          url
        }
      },
      alt
    },
    content[] {
      _type,
      _type == "imageModule" => {
        image {
          asset-> {
            url
          }
        },
        alt
      },
      _type == "smallImageModule" => {
        type,
        image {
          asset-> {
            url
          }
        },
        video {
          asset-> {
            url
          }
        },
        poster {
          asset-> {
            url
          }
        },
        alt,
        autoplay
      },
      _type == "videoModule" => {
        video {
          asset-> {
            url
          }
        },
        poster {
          asset-> {
            url
          }
        },
        alt,
        autoplay
      },
      _type == "youtubeModule" => {
        videoId,
        title,
        aspectRatio,
        autoplay,
        showControls,
        startTime
      },
      _type == "copyModule" => {
        size,
        content
      },
      _type == "imageCopyModule" => {
        image {
          asset-> {
            url
          }
        },
        alt,
        content,
        imagePosition
      },
      _type == "carouselModule" => {
        items[] {
          type,
          image {
            asset-> {
              url
            }
          },
          video {
            asset-> {
              url
            }
          },
          poster {
            asset-> {
              url
            }
          },
          alt,
          autoplay
        },
        showDots,
        showArrows,
        autoplay,
        autoplayInterval
      },
      _type == "gridModule" => {
        items[] {
          type,
          image {
            asset-> {
              url
            }
          },
          video {
            asset-> {
              url
            }
          },
          poster {
            asset-> {
              url
            }
          },
          alt,
          autoplay
        }
      }
    },
    colorScheme {
      accent,
      background,
      font
    },
    fonts[] {
      name,
      hexCode,
      usage
    }
  }`;

  const result = await client.fetch(query, { slug });
  
  if (result) {
    return {
      ...result,
      year: result.date.substring(0, 4)
    };
  }
  
  return result;
}

export async function fetchNextCaseStudy(currentSlug) {
  // First get the current case study's sortOrder
  const currentStudyQuery = `*[_type == "caseStudy" && slug.current == $currentSlug][0] {
    sortOrder,
    titleLineOne
  }`;
  
  const currentStudy = await client.fetch(currentStudyQuery, { currentSlug });
  
  if (!currentStudy) return null;

  // Then find the next case study based on sortOrder (excluding hidden projects)
  // Next means older, so we look for sortOrder < current (lower number = older)
  const nextStudyQuery = `*[_type == "caseStudy" && sortOrder < $sortOrder && !hidden] | order(sortOrder desc)[0] {
    titleLineOne,
    titleLineTwo,
    date,
    tags,
    slug,
    heroImage {
      desktop {
        asset-> {
          url
        }
      },
      mobile {
        asset-> {
          url
        }
      },
      alt
    },
    content[] {
      _type,
      _type == "imageModule" => {
        image {
          asset-> {
            url
          }
        },
        alt
      },
      _type == "smallImageModule" => {
        type,
        image {
          asset-> {
            url
          }
        },
        video {
          asset-> {
            url
          }
        },
        poster {
          asset-> {
            url
          }
        },
        alt,
        autoplay
      },
      _type == "videoModule" => {
        video {
          asset-> {
            url
          }
        },
        poster {
          asset-> {
            url
          }
        },
        alt,
        autoplay
      },
      _type == "youtubeModule" => {
        videoId,
        title,
        aspectRatio,
        autoplay,
        showControls,
        startTime
      },
      _type == "copyModule" => {
        size,
        content
      },
      _type == "imageCopyModule" => {
        image {
          asset-> {
            url
          }
        },
        alt,
        content,
        imagePosition
      },
      _type == "carouselModule" => {
        items[] {
          type,
          image {
            asset-> {
              url
            }
          },
          video {
            asset-> {
              url
            }
          },
          poster {
            asset-> {
              url
            }
          },
          alt,
          autoplay
        },
        showDots,
        showArrows,
        autoplay,
        autoplayInterval
      },
      _type == "gridModule" => {
        items[] {
          type,
          image {
            asset-> {
              url
            }
          },
          video {
            asset-> {
              url
            }
          },
          poster {
            asset-> {
              url
            }
          },
          alt,
          autoplay
        }
      }
    },
    colorScheme {
      accent,
      background,
      font
    },
    fonts[] {
      name,
      hexCode,
      usage
    }
  }`;

  let nextStudy = await client.fetch(nextStudyQuery, { 
    sortOrder: currentStudy.sortOrder
  });

  if (nextStudy) {
    nextStudy = {
      ...nextStudy,
      year: nextStudy.date.substring(0, 4)
    };
  }

  // If no next study found, get the most recent one (wrapping around, excluding hidden)
  if (!nextStudy) {
    const fallbackQuery = nextStudyQuery.replace('&& sortOrder < $sortOrder && !hidden', '&& !hidden');
    nextStudy = await client.fetch(fallbackQuery);
    if (nextStudy) {
      nextStudy = {
        ...nextStudy,
        year: nextStudy.date.substring(0, 4)
      };
    }
  }

  return nextStudy;
}

export async function fetchAllCaseStudies() {
  const query = `*[_type == "caseStudy" && !hidden] | order(sortOrder desc) {
    titleLineOne,
    titleLineTwo,
    date,
    agency,
    tags,
    slug,
    hidden,
    disabled,
    sortOrder,
    "thumbnail": coalesce(thumbnail.image.asset->url, "https://via.placeholder.com/160x120?text=No+Image"),
    "mobileThumbnail": coalesce(thumbnail.mobileImage.asset->url, thumbnail.image.asset->url, "https://via.placeholder.com/160x120?text=No+Image"),
    "hoverMedia": thumbnail.hover {
      type,
      "url": coalesce(
        video.asset->url,
        image.asset->url
      )
    }
  }`;

  const results = await client.fetch(query);
  
  // Transform the results to get year from date string
  const transformedResults = results.map(study => ({
    ...study,
    year: study.date.substring(0, 4)
  }));
  
  return transformedResults;
}

export async function fetchHomepageCaseStudies() {
  const query = `*[_type == "caseStudy" && showOnHomepage == true && !hidden] | order(sortOrder desc) {
    titleLineOne,
    titleLineTwo,
    date,
    agency,
    tags,
    slug,
    hidden,
    disabled,
    sortOrder,
    "thumbnail": coalesce(thumbnail.image.asset->url, "https://via.placeholder.com/160x120?text=No+Image"),
    "mobileThumbnail": coalesce(thumbnail.mobileImage.asset->url, thumbnail.image.asset->url, "https://via.placeholder.com/160x120?text=No+Image"),
    "hoverMedia": thumbnail.hover {
      type,
      "url": coalesce(
        video.asset->url,
        image.asset->url
      )
    }
  }`;

  const results = await client.fetch(query);
  
  // Transform the results to get year from date string
  const transformedResults = results.map(study => ({
    ...study,
    year: study.date.substring(0, 4)
  }));
  
  return transformedResults;
}

export async function fetchHomepageSnippets() {
  const query = `*[_type == "snippet" && showOnHomepage == true && !hidden] | order(date desc) {
    name,
    date,
    mediaType,
    showOnHomepage,
    media {
      type,
      image {
        asset-> {
          url
        }
      },
      video {
        asset-> {
          url
        }
      },
      alt
    },
    dualImages {
      largeImage {
        asset-> {
          url
        }
      },
      largeImageAlt,
      smallImage {
        asset-> {
          url
        }
      },
      smallImageAlt
    },
    dualVideos {
      largeVideo {
        asset-> {
          url
        }
      },
      largeVideoAlt,
      smallVideo {
        asset-> {
          url
        }
      },
      smallVideoAlt
    },
    mixedMedia {
      largeMedia {
        type,
        image {
          asset-> {
            url
          }
        },
        video {
          asset-> {
            url
          }
        },
        alt
      },
      smallMedia {
        type,
        image {
          asset-> {
            url
          }
        },
        video {
          asset-> {
            url
          }
        },
        alt
      }
    }
  }`;

  const results = await client.fetch(query);
  
  // Transform the results to get year from date string
  const transformedResults = results.map(snippet => ({
    ...snippet,
    year: snippet.date.substring(0, 4)
  }));
  
  return transformedResults;
}

export async function fetchAllSnippets() {
  const query = `*[_type == "snippet" && !hidden] | order(date desc) {
    name,
    date,
    mediaType,
    showOnHomepage,
    media {
      type,
      image {
        asset-> {
          url
        }
      },
      video {
        asset-> {
          url
        }
      },
      alt
    },
    dualImages {
      largeImage {
        asset-> {
          url
        }
      },
      largeImageAlt,
      smallImage {
        asset-> {
          url
        }
      },
      smallImageAlt
    },
    dualVideos {
      largeVideo {
        asset-> {
          url
        }
      },
      largeVideoAlt,
      smallVideo {
        asset-> {
          url
        }
      },
      smallVideoAlt
    },
    mixedMedia {
      largeMedia {
        type,
        image {
          asset-> {
            url
          }
        },
        video {
          asset-> {
            url
          }
        },
        alt
      },
      smallMedia {
        type,
        image {
          asset-> {
            url
          }
        },
        video {
          asset-> {
            url
          }
        },
        alt
      }
    }
  }`;

  const results = await client.fetch(query);
  
  // Transform the results to get year from date string
  const transformedResults = results.map(snippet => ({
    ...snippet,
    year: snippet.date.substring(0, 4)
  }));
  
  return transformedResults;
}

export async function fetchShowreel() {
  const query = `*[_type == "showreel" && isActive == true] | order(order asc)[0] {
    title,
    description,
    "previewVideoUrl": previewVideo.asset->url,
    "fullVideoUrl": fullVideo.asset->url
  }`;

  try {
    const result = await client.fetch(query);
    return result;
  } catch (error) {
    console.error('Error fetching showreel:', error);
    return null;
  }
} 

export async function fetchRouletteVideos() {
  const query = `*[_type == "rouletteVideos" && isActive == true] | order(order asc)[0] {
    title,
    "webDesignVideoUrl": webDesignVideo.asset->url,
    "motionDesignVideoUrl": motionDesignVideo.asset->url,
    "threeDDesignVideoUrl": threeDDesignVideo.asset->url
  }`;

  try {
    const result = await client.fetch(query);
    return result;
  } catch (error) {
    console.error('Error fetching roulette videos:', error);
    return null;
  }
} 